/**
 * Avviso di versione nuova.
 *
 * Il service worker serve la build che ha in cache finché uno nuovo non
 * prende il suo posto, e quello nuovo resta in attesa: senza questo avviso
 * chi ha l'app aperta, o installata, resterebbe per sempre sulla build vecchia.
 * Mai ricaricare da soli, come per `x-app-version` in `api/client.ts`: chi sta
 * registrando una partita decide quando.
 */

import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { toast } from 'sonner';
import { osservaVersione } from '../api/client.js';

/** Ogni quanto si chiede al server se c'è un service worker nuovo. */
const INTERVALLO_CONTROLLO = 60 * 60 * 1000;

export function AvvisoAggiornamento() {
  const [registrazione, setRegistrazione] = useState<ServiceWorkerRegistration>();
  const {
    needRefresh: [versioneNuova],
    updateServiceWorker,
  } = useRegisterSW({ onRegisteredSW: (_url, r) => setRegistrazione(r) });

  useEffect(() => {
    if (!registrazione) return;
    const controlla = () => void registrazione.update().catch(() => {});
    const alRitorno = () => {
      if (document.visibilityState === 'visible') controlla();
    };
    const timer = setInterval(controlla, INTERVALLO_CONTROLLO);
    document.addEventListener('visibilitychange', alRitorno);
    // L'API risponde con la versione del deploy: se cambia, il service worker
    // nuovo è già pubblicato e conviene cercarlo subito.
    const smetti = osservaVersione((disallineata) => {
      if (disallineata) controlla();
    });
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', alRitorno);
      smetti();
    };
  }, [registrazione]);

  useEffect(() => {
    if (!versioneNuova) return;
    // Resta finché non si sceglie: un avviso di sistema non sparisce da solo (§1.12).
    toast("È disponibile una versione nuova dell'app.", {
      id: 'aggiornamento',
      duration: Infinity,
      action: { label: 'Aggiorna', onClick: () => void updateServiceWorker(true) },
      cancel: { label: 'Più tardi', onClick: () => {} },
    });
  }, [versioneNuova, updateServiceWorker]);

  return null;
}
